import { useContext } from 'react'
import { Keyboard, Pressable, Text, View } from 'react-native'
import { useController, useForm } from 'react-hook-form'
import NativeQRCode from 'react-native-qrcode-svg'
import { useTranslation } from 'react-i18next'

import { isValidBankName, isValidPaymentName } from '@/utils/characters'
import { useScanQrCodeFromImage } from '@/hooks/useScanQrCodeFromImage'
import { Button, useToast } from '@/components/base'
import { FormInput } from '@/components/form-control'
import { QrCode2 } from '@/components/icon'
import { SecurityVerificationContext, SecurityVerificationTrigger } from '@/components/SecurityVerificationScreen'

export type PaymentMethodValues = {
  name: string
  bankName: string
  branchName: string
  account: string
  url: string
}

const emptyValues: PaymentMethodValues = {
  name: '',
  bankName: '',
  branchName: '',
  account: '',
  url: ''
}

type PaymentMethodFormProps = {
  kind: string
  version?: number
  paymentMethodsKeys: Record<string, string[]>
  onSubmit: (formValue: PaymentMethodValues, withdrawalPassword: string, kind: string, version?: number) => void
  defaultValues?: PaymentMethodValues
}

export function PaymentMethodForm({
  kind,
  onSubmit,
  version,
  paymentMethodsKeys,
  defaultValues = emptyValues
}: PaymentMethodFormProps) {
  const { t } = useTranslation()
  const { handleAddQrCode } = useScanQrCodeFromImage()
  const toast = useToast()
  const { control, handleSubmit, watch, setError, clearErrors } = useForm<PaymentMethodValues>({ defaultValues })
  const { setCb, open: openSecurityVerification } = useContext(SecurityVerificationContext)

  const formValues = {
    name: watch('name'),
    bankName: watch('bankName'),
    branchName: watch('branchName'),
    account: watch('account'),
    url: watch('url')
  }

  const {
    field: { value: url, onChange: onChangeUrl }
  } = useController({ control, name: 'url' })

  function handleSubmitWrapper(withdrawalPassword: string) {
    handleSubmit((formValue) => onSubmit(formValue, withdrawalPassword, kind, version))()
  }

  function validateForm() {
    Keyboard.dismiss()
    clearErrors()
    if (formValues.name.length > 100) {
      setError('name', { message: t('paymentMethod:max100Characters') })
      return
    }
    if (!isValidPaymentName(formValues.name)) {
      setError('name', { message: t('paymentMethod:noSpecialOrNumbers') })
      return
    }
    if (kind === 'bank') {
      if (formValues.bankName.length > 100) {
        setError('bankName', { message: t('paymentMethod:max100Characters') })
        return
      }
      if (formValues.branchName.length > 100) {
        setError('branchName', { message: t('paymentMethod:max100Characters') })
        return
      }
      if (!isValidBankName(formValues.bankName)) {
        setError('bankName', { message: t('paymentMethod:noSpecialCharacters') })
        return
      }
      if (!isValidBankName(formValues.branchName)) {
        setError('branchName', { message: t('paymentMethod:noSpecialCharacters') })
        return
      }
      if (!/^\d+$/.test(formValues.account)) {
        setError('account', { message: t('paymentMethod:digitsOnly') })
        return
      }
      if (formValues.account.length > 30) {
        setError('account', { message: t('paymentMethod:max30Digits') })
        return
      }
    }
    if (kind === 'alipay' && formValues.account.length > 254) {
      setError('account', { message: t('paymentMethod:max254Characters') })
      return
    }
    if (kind === 'wechat') {
      if (!/^\d+$/.test(formValues.account)) {
        setError('account', { message: t('paymentMethod:digitsOnly') })
        return
      }
      if (formValues.account.length > 15) {
        setError('account', { message: t('paymentMethod:max15Digits') })
        return
      }
    }
    if (kind === 'ermb' && (formValues.account.length !== 16 || !/^\d+$/.test(formValues.account))) {
      setError('account', { message: t('paymentMethod:exactly16Digits') })
      return
    }
    if (
      kind === 'qq' &&
      (formValues.account.length < 5 || formValues.account.length > 10 || !/^\d+$/.test(formValues.account))
    ) {
      setError('account', { message: t('paymentMethod:fiveToTenDigits') })
      return
    }
    if (paymentMethodsKeys[kind].includes(formValues.account) && formValues.account !== defaultValues.account) {
      switch (kind) {
        case 'bank': {
          setError('account', { message: t('paymentMethod:accountAlreadyAdded', { method: t('payment:bank') }) })
          return
        }
        case 'alipay': {
          setError('account', { message: t('paymentMethod:accountAlreadyAdded', { method: t('payment:alipay') }) })
          return
        }
        case 'wechat': {
          setError('account', { message: t('paymentMethod:accountAlreadyAdded', { method: t('payment:wechat') }) })
          return
        }
        case 'ermb': {
          setError('account', { message: t('paymentMethod:accountAlreadyAdded', { method: t('payment:ermb') }) })
          return
        }
        case 'qq': {
          setError('account', { message: t('paymentMethod:accountAlreadyAdded', { method: t('payment:qq') }) })
          return
        }
        default: {
          return
        }
      }
    }
    setCb(() => handleSubmitWrapper)
    openSecurityVerification()
  }

  const isButtonDisabled = getButtonStatus(formValues, defaultValues, kind)

  const selectQrCode = () => {
    Keyboard.dismiss()
    handleAddQrCode({
      onSuccess: (code) => onChangeUrl(code),
      onError: () => toast.show({ variant: 'warning', description: t('paymentMethod:qrCodeNotFound') })
    })
  }

  return (
    <View className={'gap-2 rounded-lg border border-border-10 bg-background-primary p-4'}>
      <FormInput
        control={control}
        name={'name'}
        label={t('orders:name')}
        autoCorrect={false}
        returnKeyType={'done'}
        placeholder={getNamePlaceholder(kind, t)}
      />
      {kind === 'bank' && (
        <>
          <FormInput
            control={control}
            name={'bankName'}
            label={t('orders:bankName')}
            autoCorrect={false}
            returnKeyType={'done'}
          />
          <FormInput
            control={control}
            name={'branchName'}
            label={t('orders:bankBranch')}
            autoCorrect={false}
            returnKeyType={'done'}
          />
        </>
      )}
      <FormInput
        control={control}
        name={'account'}
        label={getAccountLabel(kind, t)}
        autoCorrect={false}
        returnKeyType={'done'}
      />
      {['alipay', 'wechat', 'qq'].includes(kind) && (
        <View className={'gap-1'}>
          <Text className={'text-sm font-medium text-text-secondary'}>{getQrCodeLabel(kind, t)}</Text>
          {url === '' ? (
            <Pressable
              className={'h-[240px] items-center justify-center rounded-lg bg-fill-subtle'}
              onPress={selectQrCode}
            >
              <View className={'items-center gap-1'}>
                <QrCode2 className={'size-10 text-text-quaternary'} />
                <Text className={'text-xs text-text-secondary'}>{t('paymentMethod:uploadReceivingCode')}</Text>
              </View>
            </Pressable>
          ) : (
            <View className={'items-center gap-1'}>
              <View className={'h-[240px] items-center justify-center'}>
                <NativeQRCode value={url} size={220} />
              </View>
              <Button variant={'outline'} size={'md'} onPress={selectQrCode}>
                {t('paymentMethod:reupload')}
              </Button>
            </View>
          )}
          <View className={'h-4'} />
        </View>
      )}
      <SecurityVerificationTrigger className={'mt-2'} onPress={validateForm} disabled={isButtonDisabled}>
        {t('paymentMethod:confirm')}
      </SecurityVerificationTrigger>
    </View>
  )
}

function getNamePlaceholder(kind: string, t: (key: string) => string) {
  return t(`paymentMethod:namePlaceholder.${kind}`)
}

function getAccountLabel(kind: string, t: (key: string) => string) {
  return t(`paymentMethod:accountLabel.${kind}`)
}

function getQrCodeLabel(kind: string, t: (key: string) => string) {
  return t(`paymentMethod:receivingCodeLabel.${kind}`)
}

function getButtonStatus(formValues: PaymentMethodValues, defaultValues: PaymentMethodValues, kind: string) {
  switch (kind) {
    case 'bank': {
      return (
        formValues.name === '' ||
        formValues.bankName === '' ||
        formValues.branchName === '' ||
        formValues.account === '' ||
        (formValues.name === defaultValues.name &&
          formValues.bankName === defaultValues.bankName &&
          formValues.branchName === defaultValues.branchName &&
          formValues.account === defaultValues.account)
      )
    }
    case 'alipay':
    case 'wechat':
    case 'qq': {
      return (
        formValues.name === '' ||
        formValues.account === '' ||
        formValues.url === '' ||
        (formValues.name === defaultValues.name &&
          formValues.account === defaultValues.account &&
          formValues.url === defaultValues.url)
      )
    }
    case 'ermb': {
      return (
        formValues.name === '' ||
        formValues.account === '' ||
        (formValues.name === defaultValues.name && formValues.account === defaultValues.account)
      )
    }
    default: {
      return false
    }
  }
}
